import React, { useState, useEffect } from 'react';
import { Card, Table, Statistic, Row, Col, Spin, message, Tag, Typography, Space, Button } from 'antd';
import { ClockCircleOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getManualDebtsReport, exportManualDebtsReport } from '../../api/reports';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import { formatMoney } from '../../utils/money';
import { raporIndir } from '../../utils/downloadBlob';

// Ders seansina bagli olmayan, elle girilen gecmis donem borclari (bkz.
// StudentManualDebtController) - normal Borç/Alacak raporundan AYRI, cunku
// her kaydin kendi tarihi/aciklamasi var, ogrenci bazinda toplanmiyor.
// Yalnizca odenmemis/kismen odenmis kayitlar listelenir.
const ManualDebtsReportTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('report-manual-debts', 20);
  const [page, setPage] = useState(1);
  const [sorter, setSorter] = useState({ field: null, order: null });
  const [indiriliyor, setIndiriliyor] = useState(false);

  const fetchData = (targetPage = page, perPage = persistedPageSize, sort = sorter) => {
    setLoading(true);
    getManualDebtsReport({
      page: targetPage,
      per_page: perPage,
      sort_by: sort?.field || undefined,
      sort_direction: sort?.order === 'ascend' ? 'asc' : (sort?.order === 'descend' ? 'desc' : undefined),
    })
      .then(res => { setData(res.data); setPage(targetPage); })
      .catch(() => message.error('Geçmiş dönem borç raporu yüklenemedi.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(1, persistedPageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sunucu `export=xlsx` geldiğinde sayfalamayı yok sayıp TÜM listeyi
  // yazıyor; sıralama korunuyor (Excel ekrandaki sırayla açılıyor).
  const excelIndir = async () => {
    setIndiriliyor(true);
    try {
      await raporIndir(exportManualDebtsReport({
        sort_by: sorter?.field || undefined,
        sort_direction: sorter?.order === 'ascend' ? 'asc' : (sorter?.order === 'descend' ? 'desc' : undefined),
      }), 'gecmis-donem-borclari.xlsx');
    } catch (e) {
      message.error(e?.message || 'Excel indirilemedi.');
    } finally {
      setIndiriliyor(false);
    }
  };

  const sortProps = (field) => ({
    sorter: true,
    sortOrder: sorter.field === field ? sorter.order : null,
  });

  const columns = [
    { title: 'Öğrenci', dataIndex: 'student_name', ...sortProps('student_name') },
    { title: 'Şube', dataIndex: 'branch', ...sortProps('branch') },
    {
      title: 'Tarih',
      dataIndex: 'incurred_at',
      ...sortProps('incurred_at'),
      render: v => v ? dayjs(v).format('DD.MM.YYYY') : '—',
    },
    { title: 'Açıklama', dataIndex: 'description', ...sortProps('description') },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      align: 'right',
      ...sortProps('amount'),
      render: v => formatMoney(v),
    },
    {
      title: 'Kalan',
      dataIndex: 'remaining_amount',
      align: 'right',
      ...sortProps('remaining_amount'),
      render: (v, r) => (
        <Space orientation="vertical" size={0}>
          <span className="text-red-500 font-bold">{formatMoney(v)}</span>
          {r.payment_status === 'partial' && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {formatMoney(r.paid_amount)} tahsil edildi
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'payment_status',
      render: v => v === 'partial' ? <Tag color="warning">Kısmen Ödendi</Tag> : <Tag>Ödenmedi</Tag>,
    },
  ];

  if (loading) return <div className="text-center p-8"><Spin /></div>;

  return (
    <div>
      {data && (
        <>
          <Row gutter={16} className="mb-4">
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Ödenmemiş Kayıt Sayısı"
                  value={data.total_records}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Toplam Kalan"
                  value={parseFloat(data.total_debt_sum).toFixed(2)}
                  suffix="₺"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          <div className="mb-2 flex justify-end">
            <Button
              icon={<FileExcelOutlined />}
              onClick={excelIndir}
              loading={indiriliyor}
              disabled={!data.total}
            >
              Excel İndir
            </Button>
          </div>

          <Table
            dataSource={data.debts}
            rowKey="id"
            columns={columns}
            size="small"
            loading={loading}
            locale={{ emptyText: 'Ödenmemiş geçmiş dönem borcu yok.' }}
            onChange={(pag, _filters, nextSorter) => {
              const next = nextSorter?.order
                ? { field: nextSorter.field, order: nextSorter.order }
                : { field: null, order: null };
              const sortDegisti = next.field !== sorter.field || next.order !== sorter.order;
              setSorter(next);
              setPersistedPageSize(pag.pageSize);
              fetchData(sortDegisti ? 1 : pag.current, pag.pageSize, next);
            }}
            pagination={{
              current: data.current_page,
              pageSize: data.per_page,
              total: data.total,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (total) => `Toplam ${total} kayıt`,
            }}
          />
        </>
      )}
    </div>
  );
};

export default ManualDebtsReportTab;
